import React, {useState, useEffect, useMemo, useRef} from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale, ChartOptions, ChartData, Plugin } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData, getSatellitePrecipitation, getAnnotationPeriods } from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface DailyDataPoint {
    date: string;
    value: number;
}

interface RainfallBadSatellitePeriod {
    start: string;
    end: string;
    text: string;
}

interface PrecipitationComparisonChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
    showSatelliteComparison?: boolean;
}

const PrecipitationComparisonChart: React.FC<PrecipitationComparisonChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400,
    showSatelliteComparison = true,
}) => {
    // State management
    const [stationData, setStationData] = useState<TimeSeriesData | null>(null);
    const [satelliteData, setSatelliteData] = useState<TimeSeriesData | null>(null);
    const [badSatellitePeriods, setBadSatellitePeriods] = useState<RainfallBadSatellitePeriod[]>([]);
    const [badSatellitePeriodsCache, setBadSatellitePeriodsCache] = useState<RainfallBadSatellitePeriod[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render

     const chartRef = useRef<any>(null);

     // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch both datasets in parallel
                const [stationResponse, satelliteResponse] = await Promise.allSettled([
                    getTimeSeriesData(stationId, 'pr', startDate, endDate),
                    getSatellitePrecipitation(stationId, startDate, endDate),
                ]);

                // Handle station data response
                if (stationResponse.status === 'fulfilled') {
                    setStationData(stationResponse.value);
                } else {
                    console.error('Failed to fetch station data:', stationResponse.reason);
                }

                // Handle satellite data response
                if (satelliteResponse.status === 'fulfilled') {
                    setSatelliteData(satelliteResponse.value);
                } else {
                    console.error('Failed to fetch satellite data:', satelliteResponse.reason);
                     setSatelliteData(null);
                }


            } catch (err) {
                setError('Failed to fetch comparison data');
                console.error('Error fetching comparison data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, startDate, endDate]);

       // Separate effect for bad satellite periods - Fetch once and cache
    useEffect(() => {
        const fetchBadSatellitePeriods = async () => {
            try {
                const badSatelliteResponse =  await getAnnotationPeriods(stationId, 'rainfall_bad_satellite_periods', startDate, endDate);
                const periods = badSatelliteResponse.periods || [];

                // Always cache the fetched data
                setBadSatellitePeriodsCache(periods);

                // Only display if comparison is enabled
                if (showSatelliteComparison) {
                    setBadSatellitePeriods(periods);
                } else {
                    setBadSatellitePeriods([]);
                }
            } catch (err) {
                console.error('Failed to fetch bad satellite periods:', err);
                setBadSatellitePeriodsCache([]);
                setBadSatellitePeriods([]);
            }
        };

        fetchBadSatellitePeriods();
    }, [stationId, startDate, endDate, showSatelliteComparison]);



     // Effect to handle showing/hiding bad satellite periods based on toggle
    useEffect(() => {
        if (showSatelliteComparison) {
            // Use cached data when switching back on
            setBadSatellitePeriods(badSatellitePeriodsCache);
        } else {
            // Clear displayed periods but keep cache
            setBadSatellitePeriods([]);
        }
        // Force chart re-render
        setChartKey(prev => prev + 1);
    }, [showSatelliteComparison, badSatellitePeriodsCache]);



    // Create annotation plugin for bad satellite periods
    const badSatelliteAnnotationPlugin: Plugin<'bar'> = useMemo(() => ({
        id: 'badSatelliteAnnotation',
        beforeDraw: (chart) => {
            if (!showSatelliteComparison || !badSatellitePeriods.length) return;

            const ctx = chart.ctx;
            const chartArea = chart.chartArea;
            const xScale = chart.scales.x;

             if (showSatelliteComparison && badSatellitePeriods.length) {

                  badSatellitePeriods.forEach(period => {
                      const startX = xScale.getPixelForValue(moment(period.start).valueOf());
                    const endX = xScale.getPixelForValue(moment(period.end).valueOf());

                     if (!isNaN(startX) && !isNaN(endX)) {
                        const width = Math.max(Math.abs(endX - startX), 10);
                        const actualStartX = Math.max(Math.min(startX, endX), chartArea.left);

                        // Draw background rectangle (red)
                        ctx.save();
                        ctx.fillStyle = 'rgba(255, 193, 7, 0.15)';
                        ctx.fillRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                        // Draw border (red)
                        ctx.strokeStyle = '#ffc107';
                        ctx.lineWidth = 2;
                        ctx.setLineDash([5, 5]);
                        ctx.strokeRect(actualStartX, chartArea.top, width, chartArea.bottom - chartArea.top);

                        // Draw label
                        ctx.setLineDash([]);
                        ctx.fillStyle = '#856404';
                        ctx.font = 'bold 11px Arial';
                        ctx.textAlign = 'center';
                        const textX = actualStartX + width / 2;

                        // Draw background for text
                        const labelText = 'Precip Lower Than Satellite';
                        const textWidth = ctx.measureText(labelText).width;
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                        ctx.fillRect(textX - textWidth/2 - 4, chartArea.top + 5, textWidth + 8, 16);

                        // Draw border around text background
                        ctx.strokeStyle = '#dc3545';
                        ctx.lineWidth = 1;
                        ctx.strokeRect(textX - textWidth/2 - 4, chartArea.top + 5, textWidth + 8, 16);

                        // Draw text
                        ctx.fillStyle = '#856404';
                        ctx.fillText(labelText, textX, chartArea.top + 16);
                        ctx.restore();
                    }
                  });

             }


        }
    }), [showSatelliteComparison, badSatellitePeriods]);


    // Chart data configuration
    const chartData: ChartData<'bar'> = useMemo(() => {
        const datasets: any[] = [];

         if (stationData?.data && stationData.data.length > 0) {
            datasets.push({
                label: 'Station Precipitation (mm)',
                data: stationData.data.map(d => ({
                    x: moment(d.timestamp).valueOf(),
                    y: d.value  // Keep null values as null for gaps
                })),
                borderColor: '#1f77b4',
                backgroundColor: 'rgba(31, 119, 180, 0.7)',
                 borderWidth: 1,
                    maxBarThickness: 30,
                    categoryPercentage: 0.8,
                    barPercentage: 0.8,
                spanGaps: false  // Show gaps where data is missing
            });
        }

         if (satelliteData?.data && satelliteData.data.length > 0) {
            datasets.push({
                label: 'Satellite Precipitation (CHIRPS) (mm)',
                data: satelliteData.data.map(d => ({
                    x: moment(d.timestamp).valueOf(),
                    y: d.value  // Keep null values as null for gaps
                })),
                borderColor: '#ff7f0e',
                backgroundColor: 'rgba(255, 127, 14, 0.7)',
                 borderWidth: 1,
                    maxBarThickness: 30,
                    categoryPercentage: 0.8,
                    barPercentage: 0.8,
                spanGaps: false  // Show gaps where data is missing
            });
        }
       return { datasets };

    }, [stationData, satelliteData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'bar'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Daily Precipitation Comparison: Station vs Satellite',
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: ${value?.toFixed(3) || 0} mm`;
                    },
                    title: (context) => {
                        if (context[0] && context[0].label) {
                            return moment(Number(context[0].parsed.x)).format('DD MMM YYYY');
                        }
                        return '';
                    }
                }
            },
             // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                displayFormats: {
                    hour: 'HH:mm',
                    day: 'DD/MM',
                    week: 'DD/MM',
                    month: 'MM/YY'
                },
                tooltipFormat: 'DD MMM YYYY'
            },
            title: {
                display: true,
                text: 'Date'
            }

            },
            y: {
                title: {
                    display: true,
                    text: 'Precipitation (mm)'
                },
                beginAtZero: true,
                ticks: {
                    callback: function(value: any) {
                        return `${value.toFixed(3)} mm`;
                    }
                }
            }
        },
        elements: {
            bar: {
                borderSkipped: false,
            }
        }
    }), []);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading comparison data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // No data state
    // if (!stationData.length && !satelliteData.length) {
    //     return (
    //          <div className="card">
    //          <div className="card-body">
    //              <div className="alert alert-warning" style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    //                  <div>
    //                      <i className="fa fa-chart-bar fa-3x mb-3"></i>
    //                      <p>No precipitation data available for for comparison</p>
    //                  </div>
    //              </div>
    //          </div>
    //      </div>
    //     );
    // }

    // Main render
    return (
        <div className="card">
            <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Bar
                          ref={(ref) => {
                            chartRef.current = ref;
                        }}
                          key={chartKey} // Force re-render when key changes

                        data={chartData}
                          options={chartOptions}
                    plugins={showSatelliteComparison ? [badSatelliteAnnotationPlugin] : []}
                />
                </div>
                <div className="mt-2">
                    <small className="text-muted">
                        Station vs Satellite precipitation comparison (daily totals)

                    </small>
                </div>
            </div>
        </div>
    );
};

export default PrecipitationComparisonChart;
